/**
 * 519 Carousel - Desktop: auto-sliding with seamless infinite loop
 *               Mobile: overflow-x scroll with snap, continuous auto-scroll (same interval as desktop)
 */

const DESKTOP_BREAKPOINT = 900;

class Carousel519 extends HTMLElement {
  constructor() {
    super();
    this.viewport = null;
    this.track = null;
    this.slides = [];
    this.blocksCount = 0;
    this.currentIndex = 0;
    this.transitionTime = 5;
    this.intervalId = null;
    this.mobileTimeoutId = null;
    this.resetTimeoutId = null;
    this.isDesktop = false;
    this.mobileAutoMoved = false;
    this.transitionDuration = 500;
    this.slideWidth = 0;
    this.resizeObserver = null;
    this.blockSelectHandler = null;
  }

  connectedCallback() {
    this.viewport = this.querySelector(".carousel-519--viewport");
    this.track = this.querySelector(".carousel-519--track");
    if (!this.viewport || !this.track) return;

    this.slides = Array.from(
      this.track.querySelectorAll(
        ".carousel-519--slide:not(.carousel-519--clone)",
      ),
    );
    this.blocksCount = parseInt(
      this.getAttribute("data-blocks-count") || "0",
      10,
    );
    this.transitionTime =
      parseInt(this.getAttribute("data-transition-time") || "5", 10) * 1000;

    if (Shopify?.designMode) {
      this.initEditorMode();
      return;
    }

    if (this.blocksCount <= 1) return;

    this.isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    this.init();

    window.addEventListener("resize", this.handleResize);
  }

  disconnectedCallback() {
    this.clearTimers();
    if (!this.isDesktop) this.removeMobileListeners();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    window.removeEventListener("resize", this.handleResize);
    this.removeEditorListeners();
  }

  initEditorMode() {
    this.blockSelectHandler = (event) => {
      const target = event.target;
      if (
        target &&
        target.classList?.contains("carousel-519--slide") &&
        this.contains(target)
      ) {
        target.scrollIntoView({
          inline: "center",
          block: "nearest",
          behavior: "smooth",
        });
      }
    };
    document.addEventListener("shopify:block:select", this.blockSelectHandler);
  }

  removeEditorListeners() {
    if (this.blockSelectHandler) {
      document.removeEventListener(
        "shopify:block:select",
        this.blockSelectHandler,
      );
      this.blockSelectHandler = null;
    }
  }

  updateSlideWidth() {
    if (!this.isDesktop) return;
    this.slideWidth = this.offsetWidth / 2;
    this.style.setProperty("--slide-width", `${this.slideWidth}px`);
  }

  handleResize = () => {
    const nowDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    if (nowDesktop !== this.isDesktop) {
      if (!this.isDesktop) this.removeMobileListeners();
      this.isDesktop = nowDesktop;
      this.clearTimers();
      this.init();
    }
  };

  removeMobileListeners() {
    if (!this.viewport) return;
    this.viewport.removeEventListener("scrollend", this.mobileScrollEndHandler);
    this.viewport.removeEventListener("scroll", this.handleMobileScrollDebounced);
    clearTimeout(this.mobileScrollTimeout);
    this.mobileScrollTimeout = null;
  }

  clearTimers() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.mobileTimeoutId) {
      clearTimeout(this.mobileTimeoutId);
      this.mobileTimeoutId = null;
    }
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
  }

  init() {
    if (this.isDesktop) {
      this.initDesktop();
    } else {
      this.initMobile();
    }
  }

  initDesktop() {
    this.track.removeAttribute("data-transition");
    this.currentIndex = 0;
    this.updateSlideWidth();
    this.track.style.transform = "translateX(0)";

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isDesktop) {
        this.updateSlideWidth();
        this.track.style.transform = `translateX(-${this.currentIndex * this.slideWidth}px)`;
      }
    });
    this.resizeObserver.observe(this);

    this.startAutoSlide();
  }

  startAutoSlide() {
    if (this.intervalId) return;

    this.intervalId = setInterval(() => {
      this.advanceDesktop();
    }, this.transitionTime);
  }

  advanceDesktop() {
    if (this.blocksCount < 2) return;

    this.currentIndex += 1;
    const hasClones = this.blocksCount >= 2;
    const cloneStartIndex = this.blocksCount;

    this.track.setAttribute("data-transition", "");
    this.track.style.transform = `translateX(-${this.currentIndex * this.slideWidth}px)`;

    if (hasClones && this.currentIndex >= cloneStartIndex) {
      this.resetTimeoutId = setTimeout(() => {
        this.track.setAttribute("data-transition", "none");
        this.currentIndex = 0;
        this.track.style.transform = "translateX(0)";

        this.resetTimeoutId = null;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.track.removeAttribute("data-transition");
          });
        });
      }, this.transitionDuration);
    }
  }

  initMobile() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.mobileScrollEndHandler = this.handleMobileScrollEnd;
    this.viewport.addEventListener("scrollend", this.mobileScrollEndHandler);
    this.viewport.addEventListener("scroll", this.handleMobileScrollDebounced);

    this.intervalId = setInterval(() => {
      this.advanceMobile();
    }, this.transitionTime);
  }

  handleMobileScrollEnd = () => {
    this.checkMobileCloneReset();
  };

  mobileScrollTimeout = null;
  handleMobileScrollDebounced = () => {
    clearTimeout(this.mobileScrollTimeout);
    this.mobileScrollTimeout = setTimeout(() => {
      this.checkMobileCloneReset();
      this.mobileScrollTimeout = null;
    }, 150);
  };

  checkMobileCloneReset() {
    if (!this.viewport || this.blocksCount < 2) return;

    const slideWidth = this.viewport.offsetWidth * 0.9;
    const cloneZoneStart = this.blocksCount * slideWidth;
    const scrollLeft = this.viewport.scrollLeft;

    if (scrollLeft >= cloneZoneStart) {
      this.viewport.scrollTo({
        left: scrollLeft % cloneZoneStart,
        behavior: "auto",
      });
    }
  }

  advanceMobile() {
    if (!this.viewport || this.blocksCount < 2) return;

    const slideWidth = this.viewport.offsetWidth * 0.9;
    this.viewport.scrollBy({ left: slideWidth, behavior: "smooth" });
  }
}

customElements.define("carousel-519", Carousel519);
